import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { EmailMessageDirection } from '@/gen/cortado/v1/emails_pb'
import { formatRelativeTimeLong } from '@/lib/date-utils'

// Who spoke last on a lead's mail (CPRO-790). The backend keeps the
// timestamps; everything here is derived from them for display.

export interface MailSignalFields {
  lastInboundAt?: Timestamp
  lastOutboundAt?: Timestamp
  lastMessageDirection?: EmailMessageDirection
}

const DAY_MS = 24 * 60 * 60 * 1000

function toDate(ts?: Timestamp): Date | undefined {
  if (!ts) return undefined
  return new Date(Number(ts.seconds) * 1000 + Math.floor(ts.nanos / 1e6))
}

/** The newest message on the lead's mail, in either direction. */
export function lastContactAt(m: MailSignalFields): Date | undefined {
  const inbound = toDate(m.lastInboundAt)
  const outbound = toDate(m.lastOutboundAt)
  if (!inbound) return outbound
  if (!outbound) return inbound
  return inbound > outbound ? inbound : outbound
}

/** Whole days since the newest message, or undefined when there is no mail. */
export function quietForDays(m: MailSignalFields, now: Date = new Date()): number | undefined {
  const last = lastContactAt(m)
  if (!last) return undefined
  return Math.max(0, Math.floor((now.getTime() - last.getTime()) / DAY_MS))
}

/**
 * The lead page line: "Last heard from Jane 3 days ago" when the customer
 * wrote last, "You wrote 2 days ago" when we did, "No email yet" otherwise.
 */
export function lastContactLine(m: MailSignalFields, contactFirstName?: string, now: Date = new Date()): string {
  const last = lastContactAt(m)
  if (!last) return 'No email yet'
  // Past a week formatRelativeTimeLong gives a date ("Oct 3"), not "… ago".
  const when = formatRelativeTimeLong(last)
  const ago = now.getTime() - last.getTime() >= 7 * DAY_MS ? `on ${when}` : when
  if (m.lastMessageDirection === EmailMessageDirection.INBOUND) {
    return `Last heard from ${contactFirstName?.trim() || 'them'} ${ago}`
  }
  return `You wrote ${ago}`
}

/** Sort value for a "Last contact" column: newest first sorts highest. */
export function lastContactSortValue(m: MailSignalFields): number {
  return lastContactAt(m)?.getTime() ?? 0
}
